import { PropsWithChildren, createContext, useCallback, useContext, useState } from 'react';

import { lightTheme } from 'src/global/constants/theme.constant';
import { ProjectTheme } from 'src/global/enums/project.enum';
import { TProjectTheme } from 'src/global/types/theme.type';

type ThemeContextType = {
  theme: TProjectTheme;
  updateTheme: (newTheme: Partial<TProjectTheme>) => void;
  selectedThemeMode: ProjectTheme;
  setSelectedThemeMode: (theme: ProjectTheme) => void;
  resetToDefault: () => void;
  themeError: ThemeErrorType;
  setThemeError: (error: ThemeErrorType) => void;
  executeThemeValidation?: () => void;
};

type ThemeErrorType = Record<
  'logo' | 'favicon' | 'featureImage',
  {
    isError: boolean;
    errorMsg: string;
  }
>;

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeCustomizationProvider = (
  props: PropsWithChildren<{
    defaultTheme?: TProjectTheme;
  }>,
) => {
  const { children, defaultTheme: customTheme } = props;
  const [selectedThemeMode, setSelectedThemeMode] = useState<ProjectTheme>(ProjectTheme.LIGHT);
  const [theme, setTheme] = useState<TProjectTheme>(lightTheme || customTheme);
  const [themeError, setThemeError] = useState<ThemeErrorType>({
    logo: { isError: false, errorMsg: '' },
    favicon: { isError: false, errorMsg: '' },
    featureImage: { isError: false, errorMsg: '' },
  });

  const updateTheme = (newTheme: Partial<TProjectTheme>) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
  };

  const resetToDefault = useCallback(() => {
    setTheme(lightTheme);
    setSelectedThemeMode(ProjectTheme.LIGHT);
  }, []);

  const executeThemeValidation = useCallback(() => {
    const validations: {
      key: keyof ThemeErrorType;
      value: any;
      errorMsg: string;
    }[] = [
      {
        key: 'logo',
        value: theme.image.logo,
        errorMsg: 'Logo is required',
      },
      {
        key: 'favicon',
        value: theme.image.favicon,
        errorMsg: 'Favicon is required',
      },
    ];

    setThemeError(prev =>
      validations.reduce<ThemeErrorType>(
        (errors, { key, value, errorMsg }) => {
          errors[key] = {
            isError: !value,
            errorMsg: value ? '' : errorMsg,
          };
          return errors;
        },
        { ...prev },
      ),
    );
    const isInvalid = validations.some(({ value }) => !value);
    return isInvalid;
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        updateTheme,
        selectedThemeMode,
        setSelectedThemeMode,
        resetToDefault,
        themeError,
        setThemeError,
        executeThemeValidation,
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
